// React
import { useContext } from 'react'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

type Props = {
  items: string[]
  /** Widest layout to use; narrower screens step down from here. */
  columns?: 1 | 2 | 3
}

/**
 * The tick list shared by the audience, tailoring, takeaway and benefit
 * sections - four places that would otherwise repeat the same markup.
 */
export default function BulletGrid({ items, columns = 2 }: Props) {
  const { screenDimensions } = useContext(GlobalContext)
  const w = screenDimensions.width

  const getGrid = () => {
    // A single column is a deliberate choice, so it holds at every width.
    if (columns === 1) return 'repeat(1, 1fr)'
    if (w > breakpoints.desktopToMobile) return `repeat(${columns}, 1fr)`
    if (w > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  return (
    <Box
      component="ul"
      sx={{
        display: 'grid',
        gridTemplateColumns: getGrid(),
        gap: '0.85rem 2rem',
        listStyle: 'none',
        margin: 0,
        padding: 0
      }}
    >
      {items.map(item => (
        <Box
          component="li"
          key={item}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem' }}
        >
          <CheckIcon
            sx={{
              flexShrink: 0,
              fontSize: '1.15rem',
              color: themeColors.success.main,
              marginTop: '0.15rem'
            }}
          />
          <Typography
            sx={{
              fontSize: getFontBodySmall(),
              lineHeight: 1.6,
              color: themeColors.gray.defaultText
            }}
          >
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}
